import { Block } from '../../components/layout/Block';
import { Props } from './props';
import { LabelledInputText } from '../../../components/form/LabelledInputText';
import { CheckPermissionFunction, PermissionGate } from '../../../../Auth/components/PermissionGate';
import { CheckboxChangeEvent } from 'primereact/checkbox';
import * as React from 'react';
import { ReactElement } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { EditorialStatus } from '../../components/parts/EditorialStatus';
import { AdminDataWorkDTO, AdminDataWorkOpenAccessFundedDTO, AdminDataWorkOrcidSyncDTO } from '../../../../api/core';
import { LabelledProjectSuggester } from '../../../components/form/LabelledProjectSuggester';
import { LabelledOrganisationSuggester } from '../../../components/form/LabelledOrganisationSuggester';
import { Panel } from '../../components/layout/Panel';
import { Actions, MultiInputControls } from '../../../components/form/MultiInput/MultiInputControls';
import {
  getDefaultAdminDataWorkOpenAccessFunded,
  getDefaultAdminDataWorkOrcidSync,
} from '../../../../Settings/emptyModels';
import { InputGroup } from '../../components/layout/InputGroup';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { getVocabularyOptions } from '../../../../Vocabulary/util';
import { SelectItem } from 'primereact/selectitem';
import { LabelledInputTextarea } from '../../../components/form/LabelledInputTextarea';
import { Loading } from '../../../system/Loading';
import {
  referencedOrganisationToSelectedOrganisation,
  SelectedOrganisation,
  selectedOrganisationToReferencedOrganisation,
} from '../../components/suggester/OrganisationSuggester';
import {
  referencedProjectToSelectedProject,
  SelectedProject,
  selectedProjectToReferencedProject,
} from '../../components/suggester/ProjectSuggester';
import { TitledCheckbox } from '../../../components/form/TitledCheckbox';
import { Hidden } from '../../components/parts/Hidden';
import { LabelledInputNumber } from '../../../components/form/LabelledInputNumber';
import { LabelledCalendar } from '../../../components/form/LabelledCalendar';

export const AdministrativePage = (props: Props): ReactElement => {
  const intl: IntlShape = useIntl();
  if (!props.modifiedModel.adminDataWork.adminDataWorkOpenAccess) {
    props.setAdminField('adminDataWorkOpenAccess', {});
    return <Loading/>;
  }
  return (
    <Block title={`work.header.administrative`}>
      <div className={'col-12 lg:col-4'}>
        <PermissionGate always={true} entity={'work_generic'} field={'id'}>
          {(checkPermission: CheckPermissionFunction) => (
            <LabelledInputNumber
              id={'id'}
              autoFocus={true}
              value={props.modifiedModel.id || undefined}
              permissions={checkPermission()}
              validation={{ valid: true }}
              min={0}
              allowEmpty={true}
              maxFractionDigits={0}
              useGrouping={false}
              onChange={(): void => console.warn('ID cannot be edited')}
              title={'work.attr.id'}
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12 md:col-6 lg:col-4'}>
        <PermissionGate always={true} entity={'work_admin_data'} field={'changed'}>
          {(checkPermission: CheckPermissionFunction) => (
            <LabelledCalendar
              id={'changed'}
              value={props.modifiedModel.adminDataWork.changed}
              disabled={true}
              permissions={checkPermission()}
              validation={{ valid: true }}
              onChange={(): void => {}}
              title={'work.attr.changed'}
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12 md:col-6 lg:col-4'}>
        <PermissionGate always={true} entity={'work_admin_data'} field={'created'}>
          {(checkPermission: CheckPermissionFunction) => (
            <LabelledCalendar
              id={'created'}
              value={props.modifiedModel.adminDataWork.created}
              disabled={true}
              permissions={checkPermission()}
              validation={{ valid: true }}
              onChange={(): void => {}}
              title={'work.attr.created'}
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12 lg:col-6'}>
        <PermissionGate always={true} entity={'work_admin_data'} field={'origin'}>
          {(checkPermission: CheckPermissionFunction) => (
            <LabelledInputText
              id={'origin'}
              value={props.modifiedModel.adminDataWork.origin || ''}
              disabled={true}
              permissions={checkPermission()}
              validation={{ valid: true }}
              onChange={(): void => {}}
              title={'work.attr.origin'}
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12 lg:col-6'}>
        <PermissionGate always={true} entity={'work_admin_data'} field={'import_tenant'}>
          {(checkPermission: CheckPermissionFunction) => (
            <LabelledInputText
              id={'importTenant'}
              value={props.modifiedModel.adminDataWork.importTenant || ''}
              disabled={true}
              permissions={checkPermission()}
              validation={{ valid: true }}
              onChange={(): void => {}}
              title={'work.attr.importTenant'}
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12 lg:col-6'}>
        <PermissionGate always={true} entity={'work_admin_data'} field={'deskman'}>
          {(checkPermission: CheckPermissionFunction) => (
            <LabelledInputText
              id={'deskman'}
              value={props.modifiedModel.adminDataWork.deskman || ''}
              disabled={true}
              permissions={checkPermission()}
              validation={{ valid: true }}
              onChange={(): void => {}}
              title={'work.attr.deskman'}
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12 lg:col-6'}>
        <PermissionGate always={true} entity={'work_admin_data'} field={'hidden'}>
          {(checkPermission: CheckPermissionFunction) => (
            <Hidden
              label={'work.attr.hidden'}
              checked={props.modifiedModel.adminDataWork.hidden || false}
              disabled={!checkPermission().canWrite}
              invalid={!props.validation?.admin?.fields?.hidden.valid}
              required={checkPermission().required}
              onChange={(hidden: boolean): void => props.setAdminField('hidden', hidden)}
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12 lg:col-6'}>
        <PermissionGate entity={'work_admin_data'} field="editorial_status">
          {(checkPermission: CheckPermissionFunction) => (
            <EditorialStatus
              label={'work.attr.editorialStatus'}
              editorialStatus={props.modifiedModel.adminDataWork.editorialStatus}
              onChange={(status: string): void => props.setAdminField('editorialStatus', status)}
              checkPermission={checkPermission}
              validation={props.validation?.admin?.fields?.editorial_status}
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12'}>
        <PermissionGate entity={'work_admin_data'} field="organisations">
          {(checkPermission: CheckPermissionFunction) => (
            <LabelledOrganisationSuggester
              title={'work.attr.belongToOrganisation'}
              value={(props.modifiedModel.adminDataWork.organisations || []).map(
                referencedOrganisationToSelectedOrganisation
              )}
              onChange={(organisations: SelectedOrganisation[]): void => props.setAdminField('organisations', organisations.map(selectedOrganisationToReferencedOrganisation))}
              multiple={true}
              permissions={checkPermission()}
              id={'organisations'}
              validation={props.validation?.admin?.fields?.organisations}
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12 flex flex-column gap-1'}>
        <PermissionGate entity={'work_admin_data'} field="projects">
          {(checkPermission: CheckPermissionFunction) => (
            <LabelledProjectSuggester
              title={'work.attr.belongToProject'}
              value={(props.modifiedModel.adminDataWork.projects || []).map(
                referencedProjectToSelectedProject
              )}
              onChange={(projects: SelectedProject[]): void => props.setAdminField('projects', projects.map(selectedProjectToReferencedProject))}
              multiple={true}
              permissions={checkPermission()}
              id={'projects'}
              validation={props.validation?.admin?.fields?.projects}
            />
          )}
        </PermissionGate>
        <PermissionGate entity={'work_admin_data'} field="projects_checked">
          {(checkPermission: CheckPermissionFunction) => (
            <TitledCheckbox
              inputId={'projectsChecked'}
              checked={props.modifiedModel.adminDataWork.projectsChecked || false}
              disabled={!checkPermission().canWrite}
              invalid={!props.validation?.admin?.fields?.projects_checked.valid}
              required={checkPermission().required}
              onChange={(e): void => props.setAdminField('projectsChecked', e.checked)}
              title={'work.attr.belongToProject.checked'}
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12'}>
        <PermissionGate
          entity={'work_admin_data'}
          field="admin_data_work_open_access.admin_data_work_open_access_fundeds"
        >
          {(checkPermission: CheckPermissionFunction) => (
            <Panel title={'work.attr.adminDataWorkOpenAccess.attr.adminDataWorkOpenAccessFunded'}>
              <MultiInputControls<AdminDataWorkOpenAccessFundedDTO>
                canEdit={checkPermission().canWrite}
                lines={props.modifiedModel.adminDataWork.adminDataWorkOpenAccess.adminDataWorkOpenAccessFundeds || []}
                addNew={(rows: AdminDataWorkOpenAccessFundedDTO[]): number => rows.push(getDefaultAdminDataWorkOpenAccessFunded())}
                onChange={(adminDataWorkOpenAccessFundeds: AdminDataWorkOpenAccessFundedDTO[]): void => props.setAdminField('adminDataWorkOpenAccess', { ...props.modifiedModel.adminDataWork.adminDataWorkOpenAccess, adminDataWorkOpenAccessFundeds })}
                renderLines={(
                  fund: AdminDataWorkOpenAccessFundedDTO,
                  onLineChange: (adminDataWorkOpenAccessFundedDTO: AdminDataWorkOpenAccessFundedDTO) => void,
                  actions: Actions,
                  idx: number
                ): ReactElement => {
                  const enrichedAdminDataWork = (
                    props.enrichedValues?.adminDataWork as AdminDataWorkDTO[] | undefined
                  )?.[0];

                  const isNew = (
                    enrichedAdminDataWork
                      ?.adminDataWorkOpenAccess
                      ?.adminDataWorkOpenAccessFundeds || []
                  ).some(
                    (enrichedFund: AdminDataWorkOpenAccessFundedDTO): boolean =>
                      enrichedFund.affiliation === fund.affiliation &&
                      enrichedFund.type === fund.type
                  );
                  return (
                    <InputGroup
                      delete={{
                        action: actions.delete,
                        disabled: !checkPermission().canWrite,
                      }}
                    >
                      <LabelledDropdown
                        id={`funds-${idx}-affiliation`}
                        autoFocus={props.autoFocus}
                        value={fund.affiliation || null}
                        permissions={checkPermission('affiliation')}
                        validation={props.validation?.admin?.fields?.admin_data_work_open_access.child.fields?.admin_data_work_open_access_fundeds.children[idx].fields?.affiliation}
                        onChange={(affiliation: string): void => onLineChange({ ...fund, affiliation })}
                        label={'work.attr.adminDataWorkOpenAccess.attr.adminDataWorkOpenAccessFunded.attr.affiliation'}
                        options={getVocabularyOptions(intl.locale, 'affiliations_list')}
                        isNew={isNew}
                      />
                      <LabelledDropdown
                        id={`funds-${idx}-type`}
                        value={fund.type || null}
                        permissions={checkPermission('type')}
                        validation={props.validation?.admin?.fields?.admin_data_work_open_access.child.fields?.admin_data_work_open_access_fundeds.children[idx].fields?.type}
                        onChange={(type: string): void => onLineChange({ ...fund, type })}
                        label={'work.attr.adminDataWorkOpenAccess.attr.adminDataWorkOpenAccessFunded.attr.type'}
                        options={getVocabularyOptions(intl.locale, 'funded_list')}
                        isNew={isNew}
                      />
                    </InputGroup>
                  )
                }}
              />
            </Panel>
          )}
        </PermissionGate>
      </div>
      <div className={'col-12'}>
        <PermissionGate entity={'work_admin_data'} field="admin_data_work_open_access.access_level">
          {(checkPermission: CheckPermissionFunction) => (
            <LabelledDropdown
              id={'accessLevel'}
              className={'w-12'}
              value={props.modifiedModel.adminDataWork.adminDataWorkOpenAccess.accessLevel || null}
              permissions={checkPermission()}
              validation={props.validation?.admin?.fields?.admin_data_work_open_access?.child?.fields?.access_level}
              onChange={(accessLevel: string): void => props.setAdminField('adminDataWorkOpenAccess', { ...props.modifiedModel.adminDataWork.adminDataWorkOpenAccess, accessLevel })}
              title={'work.attr.adminDataWorkOpenAccess.attr.accessLevel'}
              options={getVocabularyOptions(intl.locale, 'access_level')}
              isNew={
                (
                  props.enrichedValues?.adminDataWork as AdminDataWorkDTO[] | undefined
                )?.[0]?.adminDataWorkOpenAccess?.accessLevel !== undefined &&
                (
                  props.enrichedValues?.adminDataWork as AdminDataWorkDTO[] | undefined
                )?.[0]?.adminDataWorkOpenAccess?.accessLevel ===
                  props.modifiedModel.adminDataWork.adminDataWorkOpenAccess?.accessLevel
              }
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12'}>
        <PermissionGate entity={'work_admin_data'} field="license">
          {(checkPermission: CheckPermissionFunction) => (
            <LabelledDropdown
              id={'license'}
              className={'w-12'}
              value={props.modifiedModel.adminDataWork.license || null}
              permissions={checkPermission()}
              validation={props.validation?.admin?.fields?.license}
              onChange={(license: string): void => props.setAdminField('license', license)}
              title={'work.attr.license'}
              options={getVocabularyOptions(intl.locale, 'license_list', props.pubtypeGroup).sort(
                (a: SelectItem, b: SelectItem): number => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1)
              )}
              isNew={
                (
                  props.enrichedValues?.adminDataWork as AdminDataWorkDTO[] | undefined
                )?.[0]?.license !== undefined &&
                (
                  props.enrichedValues?.adminDataWork as AdminDataWorkDTO[] | undefined
                )?.[0]?.license === props.modifiedModel.adminDataWork.license
              }
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12'}>
        <PermissionGate entity={'work_admin_data'} field="license_text">
          {(checkPermission: CheckPermissionFunction) => (
            <LabelledInputTextarea
              id={'licenseText'}
              className={'w-12'}
              value={props.modifiedModel.adminDataWork.licenseText || ''}
              permissions={checkPermission()}
              validation={props.validation?.admin?.fields?.license_text}
              onChange={(licenseText: string): void => props.setAdminField('licenseText', licenseText)}
              title={'work.attr.licenseText'}
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12'}>
        <PermissionGate entity={'work_admin_data'} field="license_management_status">
          {(checkPermission: CheckPermissionFunction) => (
            <LabelledDropdown
              id={'licenseManagementStatus'}
              className={'w-12'}
              value={props.modifiedModel.adminDataWork.licenseManagementStatus || null}
              permissions={checkPermission()}
              validation={props.validation?.admin?.fields?.license_management_status}
              onChange={(licenseManagementStatus: string): void => props.setAdminField('licenseManagementStatus', licenseManagementStatus)}
              title={'work.attr.licenseManagementStatus'}
              options={getVocabularyOptions(intl.locale, 'license_management_status_list')}
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12'}>
        <PermissionGate
          always={false}
          entity={'work_admin_data'}
          field={'admin_data_work_open_access.article_processing_charge'}
        >
          {(checkPermission: CheckPermissionFunction): ReactElement => (
            <LabelledInputText
              id={'articleProcessingCharge'}
              value={props.modifiedModel.adminDataWork.adminDataWorkOpenAccess.articleProcessingCharge}
              permissions={checkPermission()}
              validation={props.validation?.root?.fields?.admin_data_work_open_access?.child?.fields?.article_processing_charge}
              onChange={(articleProcessingCharge: string): void => props.setAdminField('adminDataWorkOpenAccess', { ...props.modifiedModel.adminDataWork.adminDataWorkOpenAccess, articleProcessingCharge })}
              title={'work.attr.adminDataWorkOpenAccess.attr.articleProcessingCharge'}
              isNew={
                (
                  props.enrichedValues?.adminDataWork as AdminDataWorkDTO[] | undefined
                )?.[0]?.adminDataWorkOpenAccess?.articleProcessingCharge !== undefined &&
                (
                  props.enrichedValues?.adminDataWork as AdminDataWorkDTO[] | undefined
                )?.[0]?.adminDataWorkOpenAccess?.articleProcessingCharge ===
                  props.modifiedModel.adminDataWork.adminDataWorkOpenAccess?.articleProcessingCharge
              }       
            />
          )}
        </PermissionGate>
      </div>
      <div className={'col-12'}>
        <PermissionGate entity={'work_admin_data'} field="obligatory_delivery">
          {(checkPermission: CheckPermissionFunction) => (
            <div className={'col-12 flex'}>
              <TitledCheckbox
                inputId={'obligatoryDelivery'}
                checked={props.modifiedModel.adminDataWork.obligatoryDelivery || false}
                disabled={!checkPermission().canWrite}
                invalid={!props.validation?.admin?.fields?.obligatory_delivery.valid}
                required={checkPermission().required}
                onChange={(e: CheckboxChangeEvent): void => props.setAdminField('obligatoryDelivery', e.checked)}
                title={'work.attr.obligatoryDelivery'}
              />
            </div>
          )}
        </PermissionGate>
      </div>
      <div className={'col-12'}>
        <PermissionGate entity={'work_admin_data'} field="admin_data_work_orcid_syncs">
          {(checkPermission: CheckPermissionFunction) => (
            <Panel title={intl.formatMessage({ id: 'work.attr.adminDataWorkOrcidSyncs.label' })}>
              <MultiInputControls<AdminDataWorkOrcidSyncDTO>
                canEdit={checkPermission().canWrite}
                lines={props.modifiedModel.adminDataWork.adminDataWorkOrcidSyncs || []}
                addNew={(rows: AdminDataWorkOrcidSyncDTO[]): number => rows.push(getDefaultAdminDataWorkOrcidSync())}
                onChange={(syncs: AdminDataWorkOrcidSyncDTO[]): void => props.setAdminField('adminDataWorkOrcidSyncs', syncs)}
                renderLines={(
                  syncs: AdminDataWorkOrcidSyncDTO,
                  _: (adminDataWorkOrcidSyncDTO: AdminDataWorkOrcidSyncDTO) => void,
                  actions: Actions,
                  idx: number
                ): ReactElement => (
                  <InputGroup
                    delete={{
                      action: actions.delete,
                      disabled: !checkPermission().canWrite,
                    }}
                  >
                    <LabelledInputText
                      id={`orcidSync-${idx}-orcidId`}
                      autoFocus={props.autoFocus}
                      value={syncs.orcidId || ''}
                      permissions={checkPermission('orcid_id')}
                      validation={
                        props.validation?.admin?.fields?.admin_data_work_orcid_syncs.children[idx].fields?.orcid_id
                      }
                      onChange={(): void => {}}
                      disabled={true}
                      label={'work.attr.adminDataWorkOrcidSyncs.attr.id'}
                    />
                    <LabelledInputText
                      id={`orcidSync-${idx}-orcidPutCode`}
                      value={syncs.orcidPutCode || ''}
                      permissions={checkPermission('orcid_put_code')}
                      validation={
                        props.validation?.admin?.fields?.admin_data_work_orcid_syncs.children[idx].fields
                          ?.orcid_put_code
                      }
                      onChange={(): void => {}}
                      disabled={true}
                      label={'work.attr.adminDataWorkOrcidSyncs.attr.putCode'}
                    />
                    <LabelledInputText
                      id={`orcidSync-${idx}-orcidVisibility`}
                      value={syncs.orcidVisibility || ''}
                      permissions={checkPermission('orcid_visibility')}
                      validation={
                        props.validation?.admin?.fields?.admin_data_work_orcid_syncs.children[idx].fields
                          ?.orcid_visibility
                      }
                      onChange={(): void => {}}
                      disabled={true}
                      label={'work.attr.adminDataWorkOrcidSyncs.attr.visibility'}
                    />
                  </InputGroup>
                )}
              />
            </Panel>
          )}
        </PermissionGate>
      </div>
    </Block>
  );
};
